/// <reference types="google.maps" />
import { Position } from 'geojson';

import { Injectable } from '@angular/core';

export interface DirectionsRequest {
  origin: google.maps.LatLng;
  destination: google.maps.LatLng;
  waypoints?: google.maps.DirectionsWaypoint[];
  optimizeWaypoints?: boolean;
  travelMode?: string;
}

export interface DirectionsCacheKey {
  origin: string;
  destination: string;
  waypoints: string;
  travelMode: string;
}

@Injectable({
  providedIn: 'root',
})
export class DirectionsService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private directionsService: any;
  private cache: Map<string, google.maps.DirectionsResult> = new Map();
  private readonly CACHE_PREFIX = 'directions_cache_';
  private readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  constructor() {
    this.directionsService = new google.maps.DirectionsService();
    this.loadCacheFromStorage();
  }

  /**
   * Get directions with caching support
   */
  async getDirections(request: DirectionsRequest): Promise<google.maps.DirectionsResult> {
    const cacheKey = this.generateCacheKey(request);

    // Check memory cache first
    const cachedResult = this.cache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    // Check localStorage cache
    const storedCache = this.getFromStorage(cacheKey);
    if (storedCache) {
      this.cache.set(cacheKey, storedCache);
      return storedCache;
    }

    // Make API call if not cached
    return new Promise((resolve, reject) => {
      this.directionsService.route(
        {
          origin: request.origin,
          destination: request.destination,
          waypoints: request.waypoints || [],
          optimizeWaypoints: request.optimizeWaypoints ?? true,
          travelMode: request.travelMode || 'DRIVING',
        },
        (response: google.maps.DirectionsResult, status: string) => {
          if (status === 'OK') {
            // Cache the result
            this.cache.set(cacheKey, response);
            this.saveToStorage(cacheKey, response);
            resolve(response);
          } else {
            reject(new Error(`Directions request failed due to ${status}`));
          }
        },
      );
    });
  }

  /**
   * Get directions for a route by ID with caching
   */
  async getDirectionsForRoute(
    routeId: string | number,
    request: DirectionsRequest,
  ): Promise<google.maps.DirectionsResult> {
    const cacheKey = `${this.CACHE_PREFIX}route_${routeId}`;

    // Check memory cache first
    const cachedResult = this.cache.get(cacheKey);
    if (cachedResult) {
      console.log('Cached result found', cachedResult);
      return cachedResult;
    }

    // Check localStorage cache
    const storedCache = this.getFromStorage(cacheKey);
    if (storedCache) {
      this.cache.set(cacheKey, storedCache);
      return storedCache;
    }

    // Make API call if not cached
    return new Promise((resolve, reject) => {
      this.directionsService.route(
        {
          origin: request.origin,
          destination: request.destination,
          waypoints: request.waypoints || [],
          optimizeWaypoints: request.optimizeWaypoints ?? true,
          travelMode: request.travelMode || 'DRIVING',
        },
        (response: google.maps.DirectionsResult, status: string) => {
          if (status === 'OK') {
            // Cache the result
            this.cache.set(cacheKey, response);
            this.saveToStorage(cacheKey, response);
            resolve(response);
          } else {
            reject(new Error(`Directions request failed due to ${status}`));
          }
        },
      );
    });
  }

  /**
   * Extract coordinates from directions result (handles both fresh and cached data)
   */
  extractCoordinatesFromDirectionsRoute(route: google.maps.DirectionsResult): Position[] {
    const routeOverviewPolyline = route.routes[0].overview_path;
    const positions: Position[] = routeOverviewPolyline.map((point): Position => {
      // Handle both LatLng objects (from API) and plain objects (from cache)
      if (typeof point.lng === 'function') {
        // Fresh from Google Maps API - LatLng object with methods
        const latLngPoint = point as google.maps.LatLng;
        return [latLngPoint.lng(), latLngPoint.lat()];
      } else {
        // From cache - plain object with properties
        const plainPoint = point as unknown as { lat: number; lng: number };
        return [plainPoint.lng, plainPoint.lat];
      }
    });
    return positions;
  }

  /**
   * Get detailed path from route (handles both fresh and cached data)
   */
  getDetailedPathFromRoute(route: google.maps.DirectionsResult): google.maps.LatLng[] {
    const path: google.maps.LatLng[] = [];
    for (const leg of route.routes[0].legs) {
      for (const step of leg.steps) {
        if (step.path && step.path.length > 0) {
          path.push(...step.path);
        } else {
          path.push(step.start_location);
          path.push(step.end_location);
        }
      }
    }
    return path;
  }

  /**
   * Clear cache for a specific route
   */
  clearRouteCache(routeId: string | number): void {
    const cacheKey = `${this.CACHE_PREFIX}route_${routeId}`;
    this.cache.delete(cacheKey);
    localStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cached directions
   */
  clearAllCache(): void {
    this.cache.clear();
    // Clear all directions cache from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Generate cache key from request
   */
  private generateCacheKey(request: DirectionsRequest): string {
    const originStr = this.coordinateToString(request.origin);
    const destinationStr = this.coordinateToString(request.destination);
    const waypointsStr =
      request.waypoints?.map((wp) => this.coordinateToString(wp.location as google.maps.LatLng)).join('|') || '';
    const travelMode = request.travelMode || 'DRIVING';

    return `${this.CACHE_PREFIX}${originStr}_${destinationStr}_${waypointsStr}_${travelMode}`;
  }

  /**
   * Convert coordinate to string for caching
   */
  private coordinateToString(coord: google.maps.LatLng): string {
    if (typeof coord.lat === 'function') {
      // Google Maps LatLng object
      return `${coord.lat()},${coord.lng()}`;
    } else {
      // Plain object
      return `${coord.lat},${coord.lng}`;
    }
  }

  /**
   * Save to localStorage with expiry
   */
  private saveToStorage(key: string, data: google.maps.DirectionsResult): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        expiry: Date.now() + this.CACHE_EXPIRY_MS,
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save directions cache to localStorage:', error);
    }
  }

  /**
   * Get from localStorage with expiry check
   */
  private getFromStorage(key: string): google.maps.DirectionsResult | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const cacheData = JSON.parse(stored);

      // Check if cache has expired
      if (Date.now() > cacheData.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn('Failed to load directions cache from localStorage:', error);
      return null;
    }
  }

  /**
   * Load cache from localStorage on service initialization
   */
  private loadCacheFromStorage(): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          const cached = this.getFromStorage(key);
          if (cached) {
            this.cache.set(key, cached);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load directions cache from localStorage:', error);
    }
  }
}
